import React, { useEffect, useState, useRef } from "react";
import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack, ILocalVideoTrack, IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng";
import { Box, Typography, IconButton, Paper, Button, CircularProgress } from "@mui/material";
import { Mic, MicOff, CallEnd, Person, Videocam, VideocamOff } from "@mui/icons-material";

interface AgoraCallProps {
  appId: string;
  channelName: string;
  token: string;
  uid: number;
  participants?: { id: number; name: string }[];
  onClose: () => void;
  onEndCall?: (duration: string) => void;
  isVideoCall?: boolean;
}

const AgoraCall: React.FC<AgoraCallProps> = ({ appId, channelName, token, uid, participants = [], onClose, onEndCall, isVideoCall }) => {
  const [client, setClient] = useState<IAgoraRTCClient | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<ILocalAudioTrack | null>(null);
  const [localVideoTrack, setLocalVideoTrack] = useState<ILocalVideoTrack | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [status, setStatus] = useState<"joining" | "connected" | "error">("joining");
  const [errorMessage, setErrorMessage] = useState("");
  const [startTime] = useState(Date.now());
  const localVideoRef = useRef<HTMLDivElement>(null);
  const [isVideoActive, setIsVideoActive] = useState(isVideoCall || false);

  useEffect(() => {
    if (isVideoCall || remoteUsers.some(u => u.hasVideo)) {
      setIsVideoActive(true);
    }
  }, [isVideoCall, remoteUsers]);

  useEffect(() => {
    let agoraClient: IAgoraRTCClient | null = null;
    let audioTrack: ILocalAudioTrack | null = null;
    let videoTrack: ILocalVideoTrack | null = null;

    const init = async () => {
      try {
        agoraClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        setClient(agoraClient);

        agoraClient.on("user-published", async (user, mediaType) => {
          if (agoraClient) {
            await agoraClient.subscribe(user, mediaType);
            if (mediaType === "audio") {
              user.audioTrack?.play();
            }
            setRemoteUsers((prev) => {
              if (prev.find(u => u.uid === user.uid)) return [...prev];
              return [...prev, user];
            });
          }
        });

        agoraClient.on("user-unpublished", (user) => {
          setRemoteUsers((prev) => [...prev]);
        });

        agoraClient.on("user-left", (user) => {
          setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        });

        setStatus("joining");
        console.log("Agora Initialization Info:");
        console.log("  - AppID:", appId);
        console.log("  - Channel:", channelName);
        console.log("  - Token:", token ? (token.substring(0, 10) + "...") : "NULL");
        console.log("  - UID:", uid);
        
        if (!appId || appId === "API_KEY") {
           alert("CRITICAL ERROR: AppID is missing or invalid! Value: " + appId);
        }
        
        await agoraClient.join(appId, channelName, token || null, uid || 0);
        
        if (isVideoCall) {
          const [aTrack, vTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
          audioTrack = aTrack;
          videoTrack = vTrack;
          setLocalAudioTrack(audioTrack);
          setLocalVideoTrack(videoTrack);
          await agoraClient.publish([audioTrack, videoTrack]);
        } else {
          audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
          setLocalAudioTrack(audioTrack);
          await agoraClient.publish([audioTrack]);
        }
        setStatus("connected");
      } catch (error: any) {
        console.error("Agora join failed", error);
        setStatus("error");
        setErrorMessage(error.message || "Failed to join call. Check permissions or Agora token.");
      }
    };

    init();

    return () => {
      if (audioTrack) {
        audioTrack.stop();
        audioTrack.close();
      }
      if (videoTrack) {
        videoTrack.stop();
        videoTrack.close();
      }
      if (agoraClient) {
        agoraClient.leave().catch(console.error);
      }
    };
  }, [appId, channelName, token, uid, isVideoCall]);

  useEffect(() => {
    if (localVideoTrack && localVideoRef.current && status === "connected") {
      localVideoTrack.play(localVideoRef.current);
    }
    return () => {
       if (localVideoTrack) {
         localVideoTrack.stop();
       }
    }
  }, [localVideoTrack, status]);

  useEffect(() => {
     remoteUsers.forEach(user => {
       if (user.videoTrack && user.hasVideo) {
         try {
           user.videoTrack.play(`remote-video-${user.uid}`);
         } catch (e) {
           console.error("Error playing remote video:", e);
         }
       }
     });
  }, [remoteUsers]);

  const toggleMute = () => {
    if (localAudioTrack) {
      localAudioTrack.setMuted(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const toggleVideoMute = () => {
    if (localVideoTrack) {
      localVideoTrack.setMuted(!isVideoMuted);
      setIsVideoMuted(!isVideoMuted);
    }
  };

  const handleEndCall = async () => {
    const durationMs = Date.now() - startTime;
    const mins = Math.floor(durationMs / 60000);
    const secs = Math.floor((durationMs % 60000) / 1000);
    const durationStr = `${mins}:${secs < 10 ? '0' : ''}${secs}`;

    // WhatsApp-style Group Call logic:
    // Only terminate the entire call session in the DB if you are the last person in the room.
    // Otherwise, just leave the Agora channel silently and let others continue talking.
    if (onEndCall && remoteUsers.length === 0) {
        onEndCall(durationStr);
    }

    if (localAudioTrack) {
      localAudioTrack.stop();
      localAudioTrack.close();
    }
    if (localVideoTrack) {
      localVideoTrack.stop();
      localVideoTrack.close();
    }
    if (client) {
      await client.leave();
    }
    onClose();
  };

  // isVideoActive state handles this persistently above

  return (
    <Paper
      elevation={3}
      sx={
        isVideoActive ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          backgroundColor: "#111", // Dark background for video
          display: "flex",
          flexDirection: "column",
          color: "white",
          margin: 0,
          padding: 0,
          boxSizing: "border-box"
        } : {
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 300,
          p: 2,
          borderRadius: 4,
          zIndex: 10000,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          textAlign: "center",
        }
      }
    >
      {!isVideoActive && (
        <Typography variant="h6" gutterBottom>
          Audio Call
        </Typography>
      )}

      {status === "error" ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error" variant="body2" mb={2}>{errorMessage}</Typography>
          <Button variant="outlined" size="small" onClick={onClose}>Close</Button>
        </Box>
      ) : (
        <Box sx={{ position: "relative", flexGrow: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          {status === "joining" && (
            <Box sx={{ 
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0, 
              bgcolor: "rgba(255,255,255,0.7)", zIndex: 2,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              borderRadius: 2
            }}>
              <CircularProgress size={30} sx={{ mb: 1 }} />
              <Typography variant="caption">Connecting...</Typography>
            </Box>
          )}
          
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            flexWrap: "wrap", 
            gap: isVideoActive ? 2 : 1,
            flexGrow: isVideoActive ? 1 : 0,
            p: isVideoActive ? 2 : 0,
            pb: isVideoActive ? "100px" : 0, // Padding bottom for the absolute controls
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%"
          }}>
            {isVideoActive ? (
              <>
                 {/* Local Video - You can adjust the size of the video cards right here in the 'sx' prop */}
                 <Box sx={{ position: "relative", flex: "1 1 calc(50% - 16px)", minWidth: 240, maxWidth: 600, maxHeight: "40vh", aspectRatio: "16/9", bgcolor: 'black', borderRadius: 4, overflow: 'hidden', boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                    <Box ref={localVideoRef} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    <Typography variant="caption" sx={{ position: "absolute", bottom: 12, left: 12, bgcolor: "rgba(0,0,0,0.85)", color: "#fff", fontWeight: 600, px: 1.5, py: 0.5, borderRadius: 1, zIndex: 5, boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                       You
                    </Typography>
                 </Box>

                 {/* Remote Videos - Same sizing properties applied here */}
                 {remoteUsers.map(user => {
                   const userName = participants.find(p => p.id === user.uid)?.name || `Participant ${user.uid}`;
                   return (
                     <Box key={user.uid} sx={{ position: "relative", flex: "1 1 calc(50% - 16px)", minWidth: 240, maxWidth: 600, maxHeight: "40vh", aspectRatio: "16/9", bgcolor: 'black', borderRadius: 4, overflow: 'hidden', boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                       <Box id={`remote-video-${user.uid}`} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                       <Typography variant="caption" sx={{ position: "absolute", bottom: 12, left: 12, bgcolor: "rgba(0,0,0,0.85)", color: "#fff", fontWeight: 600, px: 1.5, py: 0.5, borderRadius: 1, zIndex: 5, boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                          {userName}
                       </Typography>
                     </Box>
                   );
                 })}
              </>
            ) : (
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  boxShadow: 3,
                }}
              >
                <Person fontSize="large" />
              </Box>
            )}
          </Box>

          <Typography variant="body2" color="text.primary" mb={2}>
            {remoteUsers.length > 0
              ? `${remoteUsers.length + 1} Participant(s) Joined`
              : "Waiting for others to join..."}
          </Typography>

          <Box sx={isVideoActive ? { 
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex", 
            justifyContent: "center", 
            gap: 3, 
            p: 3,
            pb: 4,
            bgcolor: "rgba(0,0,0,0.6)",
            zIndex: 10
          } : {
            display: "flex", 
            justifyContent: "center", 
            gap: 2 
          }}>
            <IconButton
              onClick={toggleMute}
              size={isVideoActive ? "large" : "medium"}
              sx={{
                backgroundColor: isMuted ? "error.main" : (isVideoActive ? "rgba(255,255,255,0.2)" : "grey.200"),
                color: isMuted ? "white" : (isVideoActive ? "white" : "inherit"),
                "&:hover": { backgroundColor: isMuted ? "error.dark" : (isVideoActive ? "rgba(255,255,255,0.3)" : "grey.300") },
              }}
            >
              {isMuted ? <MicOff /> : <Mic />}
            </IconButton>
            
            {isVideoCall && (
              <IconButton
                onClick={toggleVideoMute}
                size={isVideoActive ? "large" : "medium"}
                sx={{
                  backgroundColor: isVideoMuted ? "error.main" : (isVideoActive ? "rgba(255,255,255,0.2)" : "grey.200"),
                  color: isVideoMuted ? "white" : (isVideoActive ? "white" : "inherit"),
                  "&:hover": { backgroundColor: isVideoMuted ? "error.dark" : (isVideoActive ? "rgba(255,255,255,0.3)" : "grey.300") },
                }}
              >
                {isVideoMuted ? <VideocamOff /> : <Videocam />}
              </IconButton>
            )}

            <IconButton
              onClick={handleEndCall}
              size={isVideoActive ? "large" : "medium"}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": { backgroundColor: "error.dark" },
              }}
            >
              <CallEnd />
            </IconButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AgoraCall;
